import {
  ObjectEventData,
  ObjectType,
  ServerEvent_Room,
} from './shared/api_shared_types';
import {
  VisibilityState,
  GameManager,
  GameObject,
  GameObjectControllerMap,
  MoveMode,
  Shape,
  type GameObjectController,
  type Size,
  type Time,
} from './types';
import {
  entityPushback,
  setParentByEntityProps,
  setPositionByEntityProps as setPositionByEntityProps,
  setVisibilityState,
  applyEntityForce,
} from './simulate';
import {
  getZeroEntity,
  getEntityPositionXY,
  removeGameObjectByEntity,
  getEntitySize as getSizeFromRelativeSize,
  updateEntityState,
  updateTransformAnimations,
  getEntityRadius,
} from './entity';
import { isZeroV2, removeDenseArrayIndex, subtractV2 } from './helpers';
import * as Space from './space';
import * as Cursor from './cursor';
import * as textObject from './textobject/textobject';
import * as textObjectSimple from './textobject/textobject_simple';
import * as textObject2 from './text_object2/text_object2';

import * as canvas from './canvas';
import * as Room from './room';

import * as textButton from './textbutton';
import * as RoomNode from './roomnode';
import * as CmsConsole from './cms_console';
import * as InfoGraphic from './infographic';
import * as UserText from './user_text';
import * as DropArea from './droparea';
import * as VideoPlayer from './videoplayer';
import * as ImageObject from './image_object';
import * as BreakoutController from './breakout_controller/breakout_controller';
import * as TextInput from './text_input';
import * as ShapeObject from './shape_object';
import * as ImageEntity from './image_entity';
import * as JourneyMap from './journey_map';
import * as Container from './container';
import * as Slider from './slider';
import * as IconButton from './icon_button';
import * as UserTextEditor from './user_text_editor';
import * as ProgressMeter from './progress_meter';
import * as ParticleSystem from './particle_system';
import * as Connection from './connection';
import * as DomObject from './dom_object';
import * as TextEditor from './text_editor';
import * as Tooltip from './tooltip';
import * as PhotoChoice from './photo_choice';
import { debugState, dt, frameNr } from '.';
import logger from './controllers/logger';

const objectControllerMap: Partial<GameObjectControllerMap> = {
  [ObjectType.SPACE]: Space.controller(),
  [ObjectType.CURSOR]: Cursor.controller(),
  [ObjectType.ROOM]: Room.controller(),
  [ObjectType.CONTAINER]: Container.controller(),
  [ObjectType.TEXTOBJECT]: textObject.controller(),
  [ObjectType.TEXTOBJECT_SIMPLE]: textObjectSimple.controller(),
  [ObjectType.TEXTOBJECT2]: textObject2.controller(),
  [ObjectType.CANVAS]: canvas.controller(),
  [ObjectType.INFOGRAPHIC]: InfoGraphic.controller(),
  [ObjectType.USER_TEXT]: UserText.controller(),
  [ObjectType.CMS_CONSOLE]: CmsConsole.controller(),
  [ObjectType.DROPAREA]: DropArea.controller(),
  [ObjectType.ROOM_NODE]: RoomNode.controller(),
  [ObjectType.SLIDER]: Slider.controller(),
  [ObjectType.VIDEOPLAYER]: VideoPlayer.controller(),
  [ObjectType.IMAGE_OBJECT]: ImageObject.controller(),
  [ObjectType.IMAGE]: ImageEntity.controller(),
  [ObjectType.JOURNEY_MAP]: JourneyMap.controller(),
  [ObjectType.BREAKOUT_CONTROLLER]: BreakoutController.controller(),
  [ObjectType.TEXT_INPUT]: TextInput.controller(),
  [ObjectType.SHAPE]: ShapeObject.controller(),
  [ObjectType.TEXT_BUTTON]: textButton.controller(),
  [ObjectType.ICON_BUTTON]: IconButton.controller(),
  [ObjectType.USER_TEXT_EDITOR]: UserTextEditor.controller(),
  [ObjectType.PROGRESS_METER]: ProgressMeter.controller(),
  [ObjectType.PARTICLE_SYSTEM]: ParticleSystem.controller(),
  [ObjectType.CONNECTION]: Connection.controller(),
  [ObjectType.DOM_ELEMENT]: DomObject.controller(),
  [ObjectType.TEXT_EDITOR]: TextEditor.controller(),
  [ObjectType.TOOLTIP]: Tooltip.controller(),
  [ObjectType.PHOTO_CHOICE]: PhotoChoice.controller(),
} as const;

export function getGameControllerMap(): Partial<GameObjectControllerMap> {
  return objectControllerMap;
}

export function syncObjects(manager: GameManager, json: ObjectEventData[]) {
  const roomEvents = json.filter((it) => it.objectType === ObjectType.ROOM);
  const canvasEvents = json.filter((it) => it.objectType === ObjectType.CANVAS);

  const otherEvents = json.filter(
    (it) => it.objectType !== ObjectType.ROOM && it.objectType !== ObjectType.CANVAS,
  );

  for (let i = 0; i < roomEvents.length; i++) {
    Room.syncRoom(manager, roomEvents[i] as ServerEvent_Room);
  }

  for (let i = canvasEvents.length - 1; i >= 0; i--) {
    const eventData = canvasEvents[i];
    const serverObjId = eventData.sId;

    const existing = manager.objectEventData[serverObjId];

    if (existing) {
      existing.push(eventData);
    } else {
      if (manager.mainRoom && manager.mainRoom.canvas) {
        manager.mainRoom.canvas.serverObjId = eventData.sId;
      }
      manager.objectEventData[serverObjId] = [eventData];
    }
  }

  const newGameObjects: GameObject[] = [];

  //create Object events & prepare baseObjects if object does not yet exist
  for (let i = otherEvents.length - 1; i >= 0; i--) {
    const eventData = otherEvents[i];
    const serverObjId = eventData.sId;

    //new syncing routine
    const existing = manager.objectEventData[serverObjId];
    if (existing) {
      //this object already has eventData to sync, add this eventData to the object's list
      existing.push(eventData);
    } else {
      //add the object with the eventData as first item
      manager.objectEventData[serverObjId] = [eventData];
    }

    let maybeyGameObject = manager.gameObjectServerIdTable[serverObjId];

    //Connect local entity to serverObjId
    if (!maybeyGameObject && 'clientId' in eventData) {
      maybeyGameObject = manager.gameObjectIdTable[eventData.clientId];
      if (maybeyGameObject) {
        maybeyGameObject.serverObjId = serverObjId;
        manager.gameObjectServerIdTable[serverObjId] = maybeyGameObject;
      }
    }

    if (!maybeyGameObject) {
      //gameObject does not exists yet on the client

      if (!eventData.full) {
        //when a gameobject is not known by the client and this is only a partial
        //update, there is no server to request a full update from — drop the event
        removeDenseArrayIndex(otherEvents, i);
        continue;
      }

      //create a new gameGameobject
      let ent = getZeroEntity();
      ent.manager = manager;
      ent.serverObjId = serverObjId;

      const objectController = objectControllerMap[eventData.objectType];
      if (objectController) {
        //grab the new gameoObject of the specific object type
        maybeyGameObject = objectController.getNew(ent);
        newGameObjects.push(maybeyGameObject);
      } else {
        console.error(
          { objectType: eventData.objectType },
          'ObjectType not found in ObjectController',
        );
      }

      if (!maybeyGameObject) {
        continue;
      }

      //Add to serverObjId table, so object can be found if referenced by other objects
      manager.gameObjectServerIdTable[serverObjId] = maybeyGameObject;
    }
  }

  for (let i = newGameObjects.length - 1; i >= 0; i--) {
    const gameObject = newGameObjects[i];
    const serverObjId = gameObject.serverObjId;
    const eventDataList = manager.objectEventData[serverObjId];

    const controller = manager.objectControllerMap[gameObject.objectType];

    for (let e = eventDataList.length - 1; e >= 0; e--) {
      const eventData = eventDataList[e];

      //initial object sync
      if (controller?.sync) {
        if (eventData.objectType === gameObject.objectType) {
          controller.sync(manager, gameObject, eventData);
        }
      }

      //setParent so it can be initialized further in the updateGameObjectHierarchy function
      setParentByEntityProps(
        manager,
        gameObject,
        serverObjId,
        eventData.roomId,
        'entityProps' in eventData ? eventData.entityProps : null,
      );

      if ('entityProps' in eventData) {
        setPositionByEntityProps(gameObject, eventData.entityProps);
        const [x, y] = getEntityPositionXY(gameObject);
        gameObject.pos.x = x;
        gameObject.pos.y = y;
      }

      if ('entityProps' in eventData && eventData.entityProps.connections) {
        for (let i = eventData.entityProps.connections.length - 1; i >= 0; i--) {
          const connectionData = eventData.entityProps.connections[i];
          Connection.syncConnection(manager, serverObjId, connectionData);
        }
      }

      removeDenseArrayIndex(eventDataList, e);
    }
  }
}

export function updateGameObjectList<t extends GameObject>(
  gameObjects: t[],
  controller: GameObjectController,
) {
  for (let i = gameObjects.length - 1; i >= 0; i--) {
    let node = gameObjects[i];

    if (node.doKill) {
      removeDenseArrayIndex(gameObjects, i);
    } else {
      controller.update(node);
    }
  }
}

export function updateGameObjectHierarchy<t extends GameObject>(
  gameObject: t,
  time: Time,
  parentSizeChanged: boolean,
  contextSize: Size,
  debug: boolean = false,
): boolean {
  const manager = gameObject.manager;

  let isDirty = false;
  let mySizeChanged = false;

  if (!manager) {
    logger.error('Object has no manager', gameObject.objectType);
    return false;
  }

  if (debug) {
    logger.debug('update', gameObject.objectType, gameObject.id, parentSizeChanged);
  }

  const controller = manager.objectControllerMap[gameObject.objectType];

  //object syncing
  if (gameObject.serverObjId) {
    const eventData = manager.objectEventData[gameObject.serverObjId];
    if (eventData) {
      for (let i = 0; i < eventData.length; i++) {
        if (controller?.sync) {
          const data = eventData[i];
          if (data.objectType === gameObject.objectType) {
            if ('entityProps' in data) {
              setPositionByEntityProps(gameObject, data.entityProps);
            }

            controller.sync(manager, gameObject, data);
            isDirty = true;
          }
        }
      }
    }
  }

  gameObject.lifeTime += dt;

  if (gameObject.autoDisappearTime > 0 && gameObject.lifeTime >= gameObject.autoDisappearTime) {
    setVisibilityState(gameObject, VisibilityState.DISAPPEARING);
  }

  if (!gameObject.anchoredPos) {
    logger.warn('no anchored pos', gameObject);
    debugState.pauseUpdate = true;
  }

  const isDisappeared = updateEntityState(gameObject, time);

  if (isDisappeared) {
    removeGameObjectByEntity(gameObject);
  }

  if (gameObject.doKill) return true;

  //ANIMATIONS
  if (updateTransformAnimations(gameObject, gameObject.animations)) {
    isDirty = true;
  }

  //AUTO-SIZE
  {
    if (gameObject.shape === Shape.CIRCLE) {
      const radius = getEntityRadius(gameObject);

      if (gameObject.radius !== radius || gameObject.width !== radius * 2) {
        gameObject.radius = radius;
        gameObject.width = radius * 2;
        gameObject.height = radius * 2;
        isDirty = true;
        gameObject.layoutChanged = true;
        mySizeChanged = true;
      }
    } else {
      const [width, height] = getSizeFromRelativeSize(gameObject, contextSize);

      if (gameObject.width !== width) {
        gameObject.width = width;
        isDirty = true;
        gameObject.layoutChanged = true;
        mySizeChanged = true;
      }
      if (gameObject.height !== height) {
        gameObject.height = height;
        isDirty = true;
        gameObject.layoutChanged = true;
        mySizeChanged = true;
      }
    }
  }

  //PHYSICS
  {
    entityPushback(gameObject.children);
  }

  {
    if (gameObject.moveMode === MoveMode.CURSOR) {
    } else if (
      gameObject === manager.cursor.draggedEntity
      // &&      gameObject !== manager.cursor.userText
    ) {
      const dragOffset = subtractV2(gameObject.parent.relativeMousePos, gameObject.dragPos);

      gameObject.anchoredPos.offset.x += dragOffset.x;
      gameObject.anchoredPos.offset.y += dragOffset.y;

      const dragParent = gameObject.parent;
      manager.cursor.draggedEntity.dragPos = dragParent.relativeMousePos;

      isDirty = true;
    } else if (!isZeroV2(gameObject.force)) {
      const [_x, _y] = applyEntityForce(gameObject, gameObject.anchoredPos.offset, dt);
      gameObject.anchoredPos.offset.x = _x;
      gameObject.anchoredPos.offset.y = _y;
    }
  }

  //AUTO-POSITION
  const [x, y] = getEntityPositionXY(gameObject);

  if (gameObject.pos.x !== x) {
    gameObject.pos.x = x;

    isDirty = true;
  }
  if (gameObject.pos.y !== y) {
    gameObject.pos.y = y;
    isDirty = true;
  }

  //OBJECT-SPECIFIC UPDATE
  if (controller && controller.update) {
    if (controller.update(gameObject, parentSizeChanged)) {
      isDirty = true;
    } else if (gameObject.layoutChanged) {
      isDirty = true;
      gameObject.layoutChanged = false;
    }
  }

  let childrenChanged = 0;

  let index = 1;
  const size = { width: gameObject.width, height: gameObject.height };
  for (let i = gameObject.children.length - 1; i >= 0; i--) {
    let child = gameObject.children[i];

    if (updateGameObjectHierarchy(child, time, mySizeChanged, size, debug)) {
      childrenChanged++;
      isDirty = true;
    }
  }

  if (controller && controller.postUpdate) {
    if (controller.postUpdate(gameObject, childrenChanged)) {
      isDirty = true;
    }
  }

  if (
    (gameObject.isDirty && gameObject.visibilityState !== VisibilityState.HIDDEN) ||
    (gameObject.isDirty && gameObject.entityStateChanged)
  ) {
    isDirty = true;
    gameObject.isDirty = false;
  } else if (
    gameObject.isDirty &&
    gameObject.visibilityState === VisibilityState.HIDDEN &&
    !gameObject.entityStateChanged
  ) {
    isDirty = false;

    gameObject.isDirty = false;
  }

  //reset
  gameObject.entityStateChanged = false;

  if (isDirty && debugState.logDirty) {
    logger.debug('dirty: ', gameObject.id, gameObject.objectType);
  }

  return isDirty;
}

export function logHierarchy(gameObject: GameObject, depth: number = 0) {

  if (gameObject.children.length) {
    for (let i = 0; i < gameObject.children.length; i++) {
      logHierarchy(gameObject.children[i], depth + 1);
    }
  }
}
